import {
  HHAlert,
  HHButton,
  HHCheckbox,
  HHFormControlLabel,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Chip, Paper } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useHingeSelectClient } from '../../api';
import { Contract, ContractState } from '../../api/types';
import { IsoDateField } from '../components/inputs/iso-date-field';
import { DisabledInfo, StepNav } from '../components/layout/step-nav';
import { TinTabs } from '../components/layout/tin-tabs';
import { SelectableCard } from '../components/selection/selectable-card';
import { ContractDecision, ContractInput } from '../context/types';
import { useStepHeader } from '../context/wizard-header.context';
import { useWizardState } from '../context/wizard-state.context';
import { rangesOverlap } from '../utils/date-overlap';
import { protoDateToIso } from '../utils/iso-to-protodate';
import { getOrgGroups, OrgGroup } from '../utils/org-groups';

const monoText = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 13,
};

interface UnmodeledFieldsPanelProps {
  input: ContractInput;
}

const UnmodeledFieldsPanel = ({
  input,
}: UnmodeledFieldsPanelProps): JSX.Element => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      borderColor: '#ffc107',
      backgroundColor: 'rgba(255, 193, 7, 0.06)',
    }}
  >
    <HHStack spacing={1}>
      <HHTypography hhVariant="subtitle2">
        Captured but not stored in provider_network
      </HHTypography>
      <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
        Save these in your contract metadata system. Phase 2 work.
      </HHTypography>
      <HHStack spacing={0.5} sx={{ pt: 1 }}>
        {input.contract_details.renewal != null && (
          <Row label="Renewal" value={input.contract_details.renewal} />
        )}
        {input.contract_details.termination_notice_standard != null && (
          <Row
            label="Termination notice (standard)"
            value={input.contract_details.termination_notice_standard}
          />
        )}
        {input.contract_details.termination_notice_fee_schedule_dispute !=
          null && (
          <Row
            label="Termination notice (FS dispute)"
            value={
              input.contract_details.termination_notice_fee_schedule_dispute
            }
          />
        )}
        {input.fee_schedule.pt_engagement_first_case_pct != null && (
          <Row
            label="PT engagement, first case"
            value={`${input.fee_schedule.pt_engagement_first_case_pct.toFixed(0)}%`}
          />
        )}
        {input.fee_schedule.pt_engagement_subsequent_case_pct != null && (
          <Row
            label="PT engagement, subsequent"
            value={`${input.fee_schedule.pt_engagement_subsequent_case_pct.toFixed(0)}%`}
          />
        )}
      </HHStack>
    </HHStack>
  </Paper>
);

const Row = ({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element => (
  <HHStack direction="row" spacing={2}>
    <HHTypography hhVariant="caption" sx={{ width: 240, opacity: 0.7 }}>
      {label}
    </HHTypography>
    <HHTypography hhVariant="caption" sx={monoText}>
      {value}
    </HHTypography>
  </HHStack>
);

// "Active" maps to EXECUTED in this proto. Per product: only DRAFT and
// EXECUTED contracts count for overlap detection — ARCHIVED is excluded.
const OVERLAP_RELEVANT_STATES = new Set<ContractState>([
  ContractState.DRAFT,
  ContractState.EXECUTED,
]);

const datesMatchExactly = (
  aStart: string,
  aEnd: string | null,
  bStart: string,
  bEnd: string | null,
): boolean => aStart === bStart && (aEnd ?? null) === (bEnd ?? null);

interface ContractOrgCardProps {
  group: OrgGroup;
  legalName: string;
  inputStart: string;
  inputEnd: string;
  clientName: string;
}

// Per-TIN contract decision card. Each card runs its own overlap-detection
// against the org's customerAccount, holds its own ContractDecision, and
// gates its own continue-readiness. Single-TIN input renders one card with
// the same UX as pre-PR-2.
const ContractOrgCard = ({
  group,
  legalName,
  inputStart,
  inputEnd,
  clientName,
}: ContractOrgCardProps): JSX.Element => {
  const { state, setState } = useWizardState();
  const client = useHingeSelectClient();
  const fired = useRef(false);
  const [overlapStatus, setOverlapStatus] = useState<
    'pending' | 'loaded' | 'error'
  >('pending');
  const [overlapError, setOverlapError] = useState<string | null>(null);
  const [conflicting, setConflicting] = useState<Contract[]>([]);

  const tin = group.tax_id;
  const decision = state.decisions.contract[tin];
  const customerAccount = state.decisions.customerAccount[tin];

  // Seed a default create-mode decision on first entry.
  useEffect(() => {
    if (decision != null) return;
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        contract: {
          ...s.decisions.contract,
          [tin]: {
            mode: 'create',
            name: `${clientName} ${inputStart.slice(0, 4)}`,
            effectiveStart: inputStart,
            effectiveEnd: inputEnd,
            openEnded: false,
          },
        },
      },
    }));
  }, [decision, clientName, inputEnd, inputStart, setState, tin]);

  // One-shot overlap check against this org's customerAccount when reusing it.
  useEffect(() => {
    if (fired.current || customerAccount?.mode !== 'reuse') return;
    fired.current = true;
    void (async (): Promise<void> => {
      try {
        const result = await client.listContractsForCustomerAccount({
          customerAccountId: customerAccount.id,
        });
        const overlapping = (result.contracts ?? []).filter(c => {
          if (!OVERLAP_RELEVANT_STATES.has(c.state)) return false;
          const cStart = protoDateToIso(c.effectiveStart);
          const cEnd = c.effectiveEnd ? protoDateToIso(c.effectiveEnd) : null;
          return rangesOverlap(inputStart, inputEnd, cStart, cEnd);
        });
        setConflicting(overlapping);
        setOverlapStatus('loaded');
      } catch (e) {
        setOverlapError((e as Error).message);
        setOverlapStatus('error');
      }
    })();
  }, [client, customerAccount, inputEnd, inputStart]);

  // For "create-mode" customer accounts the overlap check can't run (no
  // existing customerAccountId yet). Mark loaded so gating clears.
  useEffect(() => {
    if (customerAccount?.mode === 'create' && overlapStatus === 'pending') {
      setOverlapStatus('loaded');
    }
  }, [customerAccount, overlapStatus]);

  const recommendedId = useMemo(() => {
    const exact = conflicting.find(c =>
      datesMatchExactly(
        protoDateToIso(c.effectiveStart),
        c.effectiveEnd ? protoDateToIso(c.effectiveEnd) : null,
        inputStart,
        inputEnd,
      ),
    );
    return exact?.id ?? null;
  }, [conflicting, inputEnd, inputStart]);

  const pickReuse = (c: Contract): void => {
    const existingStart = protoDateToIso(c.effectiveStart);
    const existingEnd = c.effectiveEnd ? protoDateToIso(c.effectiveEnd) : null;
    const exactMatch = datesMatchExactly(
      existingStart,
      existingEnd,
      inputStart,
      inputEnd,
    );
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        contract: {
          ...s.decisions.contract,
          [tin]: {
            mode: 'reuse',
            existingId: c.id ?? '',
            existingName: c.name,
            effectiveStart: existingStart,
            effectiveEnd: existingEnd,
            datesDifferAcknowledged: exactMatch,
          },
        },
      },
    }));
  };

  const pickCreateNew = (): void => {
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        contract: {
          ...s.decisions.contract,
          [tin]: {
            mode: 'create',
            name: `${clientName} ${inputStart.slice(0, 4)}`,
            effectiveStart: inputStart,
            effectiveEnd: inputEnd,
            openEnded: false,
          },
        },
      },
    }));
  };

  const updateCreateField = <
    K extends keyof Extract<ContractDecision, { mode: 'create' }>,
  >(
    field: K,
    value: Extract<ContractDecision, { mode: 'create' }>[K],
  ): void => {
    setState(s => {
      const cur = s.decisions.contract[tin];
      if (cur == null || cur.mode !== 'create') return s;
      return {
        ...s,
        decisions: {
          ...s.decisions,
          contract: {
            ...s.decisions.contract,
            [tin]: { ...cur, [field]: value },
          },
        },
      };
    });
  };

  const setAcknowledged = (v: boolean): void => {
    setState(s => {
      const cur = s.decisions.contract[tin];
      if (cur == null || cur.mode !== 'reuse') return s;
      return {
        ...s,
        decisions: {
          ...s.decisions,
          contract: {
            ...s.decisions.contract,
            [tin]: { ...cur, datesDifferAcknowledged: v },
          },
        },
      };
    });
  };

  const hasOverlap = conflicting.length > 0;
  const reuseDatesDiffer =
    decision?.mode === 'reuse' &&
    !datesMatchExactly(
      decision.effectiveStart,
      decision.effectiveEnd,
      inputStart,
      inputEnd,
    );

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <HHStack spacing={2}>
        <HHStack direction="row" spacing={2} alignItems="baseline">
          <HHTypography hhVariant="subtitle1">{legalName}</HHTypography>
          <HHTypography hhVariant="caption" sx={monoText}>
            TIN {tin}
          </HHTypography>
        </HHStack>

        {overlapStatus === 'error' && (
          <HHAlert hhVariant="outlined" severity="warning">
            Couldn&apos;t check for overlapping contracts: {overlapError}.
            Continuing will create a new contract — verify manually before
            committing.
          </HHAlert>
        )}

        {hasOverlap && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <HHStack spacing={2}>
              <HHStack spacing={0.5}>
                <HHTypography hhVariant="subtitle2">
                  Existing contracts overlap your input dates ({inputStart} →{' '}
                  {inputEnd})
                </HHTypography>
                <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
                  Reuse one of the existing contracts below, or create a new
                  contract anyway. The recommended option matches your input
                  dates exactly.
                </HHTypography>
              </HHStack>
              <div
                role="radiogroup"
                aria-label="Choose existing contract or create new"
              >
                <HHStack spacing={1}>
                  {conflicting.map(c => {
                    const cId = c.id ?? '';
                    const isPicked =
                      decision?.mode === 'reuse' && decision.existingId === cId;
                    const isRecommended = cId === recommendedId;
                    const cStart = protoDateToIso(c.effectiveStart);
                    const cEnd = c.effectiveEnd
                      ? protoDateToIso(c.effectiveEnd)
                      : null;
                    return (
                      <SelectableCard
                        key={cId}
                        selected={isPicked}
                        onClick={(): void => pickReuse(c)}
                        recommended={isRecommended}
                        recommendedLabel="Recommended · exact date match"
                      >
                        <HHStack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <HHTypography hhVariant="body1">
                            {c.name}
                          </HHTypography>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={c.state.replace('CONTRACT_STATE_', '')}
                          />
                        </HHStack>
                        <HHTypography
                          hhVariant="caption"
                          sx={{ ...monoText, opacity: 0.75 }}
                        >
                          {cStart} → {cEnd ?? 'open-ended'}
                        </HHTypography>
                        <HHTypography
                          hhVariant="caption"
                          sx={{ ...monoText, opacity: 0.55 }}
                        >
                          id: {cId}
                        </HHTypography>
                      </SelectableCard>
                    );
                  })}
                  <SelectableCard
                    selected={decision?.mode === 'create'}
                    onClick={pickCreateNew}
                  >
                    <HHTypography hhVariant="body1">
                      Create a new contract anyway
                    </HHTypography>
                    <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
                      Use this if the existing contracts don&apos;t cover your
                      case (different LOB, different program, etc.).
                    </HHTypography>
                  </SelectableCard>
                </HHStack>
              </div>

              {decision?.mode === 'reuse' && reuseDatesDiffer && (
                <HHAlert hhVariant="outlined" severity="warning">
                  <HHStack spacing={1}>
                    <HHTypography hhVariant="subtitle2">
                      Date mismatch
                    </HHTypography>
                    <HHTypography hhVariant="caption" sx={monoText}>
                      Existing: {decision.effectiveStart} →{' '}
                      {decision.effectiveEnd ?? 'open-ended'}
                    </HHTypography>
                    <HHTypography hhVariant="caption" sx={monoText}>
                      Your input: {inputStart} → {inputEnd}
                    </HHTypography>
                    <HHFormControlLabel
                      label="I understand the existing contract's dates differ from my input and want to attach the new fee schedule / service providers to it anyway."
                      control={
                        <HHCheckbox
                          hhVariant="variant-bypass"
                          checked={decision.datesDifferAcknowledged}
                          onChange={(_, v): void => setAcknowledged(v)}
                        />
                      }
                    />
                  </HHStack>
                </HHAlert>
              )}
            </HHStack>
          </Paper>
        )}

        {decision?.mode === 'create' && (
          <HHStack spacing={3}>
            <HHTextField
              hhVariant="variant-bypass"
              label="Contract name"
              value={decision.name}
              onChange={(e): void => updateCreateField('name', e.target.value)}
              fullWidth
            />
            <HHStack direction="row" spacing={3}>
              <IsoDateField
                label="Effective start"
                value={decision.effectiveStart}
                onChange={(v): void =>
                  updateCreateField('effectiveStart', v ?? '')
                }
              />
              <IsoDateField
                label="Effective end"
                value={decision.effectiveEnd}
                onChange={(v): void => updateCreateField('effectiveEnd', v)}
                disabled={decision.openEnded}
                minDate={decision.effectiveStart}
              />
            </HHStack>
            <HHFormControlLabel
              label="Open-ended (auto-renew, no end date)"
              control={
                <HHCheckbox
                  hhVariant="variant-bypass"
                  checked={decision.openEnded}
                  onChange={(_, v): void => {
                    updateCreateField('openEnded', v);
                    if (v) updateCreateField('effectiveEnd', null);
                    else updateCreateField('effectiveEnd', inputEnd);
                  }}
                />
              }
            />
            {customerAccount && (
              <HHStack direction="row" spacing={4} alignItems="baseline">
                <HHTypography hhVariant="caption" sx={{ opacity: 0.6 }}>
                  Customer account
                </HHTypography>
                <HHTypography hhVariant="body2" sx={monoText}>
                  {customerAccount.mode === 'reuse'
                    ? customerAccount.id
                    : '(create new)'}
                </HHTypography>
              </HHStack>
            )}
          </HHStack>
        )}

        {decision?.mode === 'reuse' && (
          <HHStack spacing={2}>
            <HHTypography hhVariant="subtitle2">
              Reusing existing contract
            </HHTypography>
            <HHStack direction="row" spacing={4}>
              <HHTypography
                hhVariant="caption"
                sx={{ width: 140, opacity: 0.6 }}
              >
                Name
              </HHTypography>
              <HHTypography hhVariant="body2" sx={monoText}>
                {decision.existingName}
              </HHTypography>
            </HHStack>
            <HHStack direction="row" spacing={4}>
              <HHTypography
                hhVariant="caption"
                sx={{ width: 140, opacity: 0.6 }}
              >
                Existing dates
              </HHTypography>
              <HHTypography hhVariant="body2" sx={monoText}>
                {decision.effectiveStart} →{' '}
                {decision.effectiveEnd ?? 'open-ended'}
              </HHTypography>
            </HHStack>
            <HHStack direction="row" spacing={4}>
              <HHTypography
                hhVariant="caption"
                sx={{ width: 140, opacity: 0.6 }}
              >
                Existing id
              </HHTypography>
              <HHTypography hhVariant="body2" sx={monoText}>
                {decision.existingId}
              </HHTypography>
            </HHStack>
            <HHStack direction="row" spacing={2} sx={{ pt: 1 }}>
              <HHButton
                hhVariant="variant-bypass"
                variant="outlined"
                color="primary"
                onClick={pickCreateNew}
              >
                Switch to creating a new contract instead
              </HHButton>
            </HHStack>
          </HHStack>
        )}
      </HHStack>
    </Paper>
  );
};

interface ContractInnerProps {
  input: ContractInput;
}

const ContractInner = ({ input }: ContractInnerProps): JSX.Element => {
  const { state } = useWizardState();
  const orgGroups = getOrgGroups(input);
  const inputStart = input.contract_details.effective_date;
  const inputEnd = input.contract_details.term_end;
  const [activeTin, setActiveTin] = useState<string>(
    () => orgGroups[0]?.tax_id ?? '',
  );

  useStepHeader({
    description:
      orgGroups.length > 1
        ? `Pick whether each TIN's onboarding wires up a new Contract or reuses an existing one. The ${orgGroups.length} contracts are independent — each can reuse different existing contracts, or all create new.`
        : 'Pick whether this onboarding wires up a new Contract or reuses an existing one. Reusing skips Create-contract on commit and attaches downstream fee schedules / CSPs to the existing record.',
  });

  const isReadyForTin = (tin: string): boolean => {
    const d = state.decisions.contract[tin];
    if (d == null) return false;
    if (d.mode === 'create') return d.name.trim().length > 0;
    if (d.mode === 'reuse') {
      if (d.existingId === '') return false;
      const datesDiffer = !datesMatchExactly(
        d.effectiveStart,
        d.effectiveEnd,
        inputStart,
        inputEnd,
      );
      if (datesDiffer && !d.datesDifferAcknowledged) return false;
    }
    return true;
  };

  // Per-TIN gating: every group's contract decision must be set, with valid
  // shape, and any reuse-mode date mismatch must be acknowledged.
  const allReady = orgGroups.every(g => isReadyForTin(g.tax_id));

  const canContinue = allReady;
  const disabledInfo: DisabledInfo | undefined = canContinue
    ? undefined
    : {
        message:
          orgGroups.length > 1
            ? 'Each TIN needs a contract decision (and any date mismatches acknowledged) before continuing.'
            : 'Initializing contract decision…',
        severity: 'info',
      };

  return (
    <HHStack spacing={3}>
      <TinTabs
        items={orgGroups.map(g => ({
          group: g,
          legalName:
            state.decisions.orgNames[g.tax_id] ?? input.entity_legal_name,
          ready: isReadyForTin(g.tax_id),
        }))}
        activeTin={activeTin}
        onSelect={setActiveTin}
      />

      {orgGroups.map(g => {
        const isActive = g.tax_id === activeTin || orgGroups.length === 1;
        if (!isActive) return null;
        return (
          <ContractOrgCard
            key={g.tax_id}
            group={g}
            legalName={
              state.decisions.orgNames[g.tax_id] ?? input.entity_legal_name
            }
            inputStart={inputStart}
            inputEnd={inputEnd}
            clientName={input.client_name}
          />
        );
      })}

      <UnmodeledFieldsPanel input={input} />

      <StepNav
        id="contract"
        canContinue={canContinue}
        disabledInfo={disabledInfo}
      />
    </HHStack>
  );
};

export const ContractStep = (): JSX.Element => {
  const { state } = useWizardState();
  if (state.input == null) return <Navigate to="../intake" replace />;
  return <ContractInner input={state.input} />;
};
